<script setup>

    import LatteDrawer from '@/Components/App/LatteDrawer.vue'
    import Modal from '@/Components/App/Modal.vue'
    import QuickSearch from '@/Components/App/QuickSearch.vue'
    import LatteCareScheduleSearch from '@/Components/Client/LatteCareScheduleSearch.vue'
    import LatteClientCareNeedSelection from '@/Components/Client/LatteClientCareNeedSelection.vue'
    import LatteClientCriteriaList from '@/Components/Client/LatteClientCriteriaList.vue'
    import AddressAutocomplete from '@/Components/Forms/AddressAutocomplete.vue'
    import InputLabel from '@/Components/Forms/InputLabel.vue'
    import LwxSimpleSelect from '@/Components/Forms/LwxSimpleSelect.vue'
    import LwxSwitch from '@/Components/Forms/LwxSwitch.vue'
    import PrimaryButton from '@/Components/Forms/PrimaryButton.vue'
    import SecondaryButton from '@/Components/Forms/SecondaryButton.vue'
    import TextInput from '@/Components/Forms/TextInput.vue'
    import LatteSearchClientSummaryCard from '@/Components/LatteSearchClientSummaryCard.vue'
    import Loader from '@/Components/App/Loader.vue'
    import MultiSelect from 'primevue/multiselect'
    import Skeleton from 'primevue/skeleton'
    import { deepCopy, parseAddressComponents, util, SERVICE_TYPES_OPTIONS } from '@/Helpers'
    import { debounce, throttle } from 'lodash'
    import { useCaregiverSearchStore, useClientSingleStore, useConfigStore, useNotificationStore } from '@/Stores'
    import { ArrowUturnLeftIcon, MagnifyingGlassIcon, ChevronUpIcon } from '@heroicons/vue/24/solid'
    import { ArrowDownIcon } from '@heroicons/vue/20/solid'
    import { ArrowTopRightOnSquareIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
    import { IconPersonSearch16Filled, IconSnooze16Filled } from '@iconify-prerendered/vue-fluent'
    import CaregiverSearchResults from '@/Components/Caregiver/CaregiverSearchResults.vue'
    import { useMouse } from '@vueuse/core'
    import { useNProgress } from '@vueuse/integrations/useNProgress'
    import { storeToRefs } from 'pinia'
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
    import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router'
    import { initializeOrdinalChoiceOrders } from '@/Utils/criteriaMatching'

    const router = useRouter()
    const route = useRoute()
    const configStore = useConfigStore()
    const { config, criteriaNameDict, clientCriterias } = storeToRefs(configStore)

    const clientSingleStore = useClientSingleStore()
    const { client, isFetching: loading } = storeToRefs(clientSingleStore)

    const caregiverSearchStore = useCaregiverSearchStore()
    const {
        caregivers, totalCount, preferredSortOrder,
        searchState, currentSearchPayload,
        isRequestPending, isSearching, noMoreResult, hasLoadedSecondPage, limitOffset,
        searchMustHaves, searchWowFactors,
        hasUnsyncedChanges, hasDefaultSearchState,
    } = storeToRefs(caregiverSearchStore)

    const notificationStore = useNotificationStore()

    // Global progress bar — active when client data or search results are loading
    const { isLoading: nProgressLoading } = useNProgress()
    watch([isRequestPending, loading], ([searchPending, clientLoading]) => {
        nProgressLoading.value = searchPending || clientLoading
    })

    // Initialize ordinal choice orders for criteria matching once config is loaded
    watch(criteriaNameDict, (dict) => {
        if (dict && Object.keys(dict).length > 0) {
            initializeOrdinalChoiceOrders(dict)
        }
    }, { immediate: true })

    let shouldApplyClientSearchCareSchedule = true
    let shouldApplyClientLocation = true
    let shouldApplyClientCriteria = true
    let shouldApplyClientServiceTypes = true

    const INFINITE_SCROLL_THRESHOLD = -1000

    const isPlaceholderClient = computed(() => {
        return !client.value || 'fe_generated' in client.value;
    })

    const props = defineProps({
        client_id: {
            type: [String, Number],
            default: null
        }
    })

    // Client prefill related logics
    onMounted(() => {
        const clientId = props.client_id || new URLSearchParams(window.location.search).get('client_id')
        if (clientId) {
            clientSingleStore.fetchForSearch(clientId, ['latest_rating_per_job', 'search_care_schedule', 'service_dates', 'recent_caregivers', 'service_types'])
        } else {
            // No client route — clear any stale client/search state from previous session
            if (!isPlaceholderClient.value) {
                clientSingleStore.reset()
            }
            caregiverSearchStore.resetSearchState()
            // Set default service types when no client is selected
            searchState.value.service_types = ['elder_care']
        }

        // Auto-set "Mine Only" filter when on the mine route
        if (route.name === 'search.caregivers.mine') {
            searchState.value.all_candidates = false
            if (!clientId) {
                handleSearch()
            }
        }

        window.addEventListener('scroll', handleScrollEvents)
        window.addEventListener('scroll', handleSortMenuScroll, true)
    })

    let skipRouteLeaveGuard = false

    /**
     * Fully resets all local search context: search state, results, pagination.
     * Call this before loading a new client or clearing the search entirely.
     */
    function resetSearchContext() {
        // Reset one-shot hydration flags so new client data will be applied
        shouldApplyClientSearchCareSchedule = true
        shouldApplyClientLocation = true
        shouldApplyClientCriteria = true
        shouldApplyClientServiceTypes = true

        // Reset store-managed search state + results
        caregiverSearchStore.resetSearchState()
        searchState.value.sort_order = resolveSortOrder()

        // Reset local availability and schedule snapshot (prevents stale schedule from previous client)
        localizedAvailability.value = []
        clientSchedule.value = null
        resetScheduleHovered.value = false
        scheduleComponentKey.value++
    }

    const clearClient = () => {
        clientSingleStore.reset()
        resetSearchContext()
        router.push({ name: 'search.caregivers' })
    }

    function loadClientForSearch(clientId) {
        clientSingleStore.reset()
        resetSearchContext()
        clientSingleStore.fetchForSearch(clientId, ['latest_rating_per_job', 'search_care_schedule', 'service_dates', 'recent_caregivers', 'service_types'])
    }

    watch(() => props.client_id, (newValue, oldValue) => {
        // Skip the initial immediate invocation — onMounted handles the first load
        if (oldValue === undefined) return;

        if (oldValue != null && newValue === null) {
            clearClient()
        } else if (newValue != null && newValue != oldValue) {
            loadClientForSearch(newValue)
        }
    }, { immediate: true })

    // Care schedule related logics
    const DEFAULT_LIVE_OUT_START_HOUR = 9
    const DEFAULT_LIVE_OUT_END_HOUR = 17

    const stayType = [
        { value: '', name: 'Either' },
        { value: 'live_in', name: 'Live-in' },
        { value: 'live_out', name: 'Live-out' }
    ]
    let isSchedulePreviewActive = false
    const handleCareScheduleChange = (newSchedule) => {
        if (isSchedulePreviewActive) return;
        searchState.value = { ...searchState.value, ...newSchedule }
        if (newSchedule.availability) {
            localizedAvailability.value = newSchedule.availability
        }
    }

    const localizedAvailability = ref([])

    // Perform search related logics
    const handleSearch = (event) => {

         if (hasUnsyncedChanges.value || (event instanceof MouseEvent && event.target?.closest('#searchButton'))) {
            caregiverSearchStore.resetPagination()
            caregivers.value = []
            util.scrollToTop()
        }

        if (noMoreResult.value) {
            return;
        }

        const searchPayload = deepCopy(Object.assign({}, searchState.value, limitOffset.value))

        if (!searchState.value.living_preference) {
            searchPayload.availability = []
        }

        if (searchState.value.living_preference === 'live_in') {
            searchPayload.availability?.forEach(slot => {
                delete slot.start_hour
                delete slot.end_hour
            })
        }

        if (!searchState.value.max_travel_time) {
            delete searchPayload.max_travel_time
        }

        if (!searchState.value.state) {
            delete searchPayload.state
        }

        if (!searchState.value.in_state_only) {
            delete searchPayload.in_state_only
        }

        if (searchState.value.zip || searchState.value.state || (searchState.value.city && searchState.value.state)) {
            const destination = {}
            if (searchState.value.address) destination.address = searchState.value.address
            if (searchState.value.city) destination.city = searchState.value.city
            if (searchState.value.state) destination.state = searchState.value.state
            if (searchState.value.zip) destination.zip = searchState.value.zip
            searchPayload.destination = destination
        }
        delete searchPayload.zip
        delete searchPayload.address
        delete searchPayload.city
        delete searchPayload.state

        if (searchMustHaves.value.length > 0) {
            searchPayload.must_have = caregiverSearchStore.transformCriteriasToPayloadFormat(searchMustHaves.value);
        }

        if (searchWowFactors.value.length > 0) {
            searchPayload.wow = caregiverSearchStore.transformCriteriasToPayloadFormat(searchWowFactors.value);
        }

        delete searchPayload.criterias

        searchPayload.my_candidate = !searchState.value.all_candidates
        delete searchPayload.all_candidates

        // Delete service_types if empty to avoid unnecessary filtering
        if (!searchState.value.service_types || searchState.value.service_types.length === 0) {
            delete searchPayload.service_types
        }

        searchPayload.sort_order = searchState.value.sort_order.value || searchState.value.sort_order || 'smart'

        if (isPlaceholderClient.value) {
            delete searchPayload.client_id
        }

        caregiverSearchStore.snapshotSearchState()
        caregiverSearchStore.search(searchPayload)
            .then(matchingCaregivers => {
                searchPayload.must_have = searchMustHaves.value;
                searchPayload.wow = searchWowFactors.value;
                currentSearchPayload.value = deepCopy(searchPayload)
                if (matchingCaregivers.length === limitOffset.value.limit) {
                    limitOffset.value.offset += limitOffset.value.limit
                    if (limitOffset.value.offset > limitOffset.value.limit) {
                        hasLoadedSecondPage.value = true
                    }
                } else {
                    noMoreResult.value = true
                }
            })
            .catch(error => {
                if (axios.isCancel(error?.error)) return;
                notificationStore.add(`Search Failed`, 'error', `Please try again`, 5000)
            })
    }

    const debouncedHandleSearch = debounce((event) => {
        handleSearch(event)
    }, 300)

    const _handleScrollEvents = () => {
        const bottomDistance = window.innerHeight + window.scrollY - document.documentElement.offsetHeight
        if (bottomDistance > INFINITE_SCROLL_THRESHOLD && !isRequestPending.value && caregivers.value.length > 0) {
            handleSearch()
        }
    }
    const handleScrollEvents = throttle(_handleScrollEvents, 200)

    const sortMenuRef = ref(null)
    const sortButtonRef = ref(null)
    const sortMenuOpen = ref(false)

    const toggleSortMenu = (event) => {
        sortMenuRef.value.toggle(event)
    }
    const showSortMenu = (event) => {
        sortMenuRef.value.show(event)
    }
    const hideSortMenu = () => {
        if (sortMenuRef.value?.overlayVisible) {
            sortMenuRef.value.hide()
        }
    }

    // Close sort menu on page scroll (instant, no debounce)
    const handleSortMenuScroll = () => {
        if (sortMenuOpen.value) {
            hideSortMenu()
        }
    }

    // Close sort menu when mouse moves far from button + menu
    const SORT_MENU_MOUSE_THRESHOLD = 200
    const { x: mouseX, y: mouseY } = useMouse({ type: 'client' })

    watch([mouseX, mouseY], ([mx, my]) => {
        if (!sortMenuOpen.value) return;

        const btnRect = sortButtonRef.value?.getBoundingClientRect()
        const menuRect = sortMenuRef.value?.container?.getBoundingClientRect()
        if (!btnRect) return;

        const inRange = (rect) => {
            const dx = Math.max(rect.left - mx, 0, mx - rect.right)
            const dy = Math.max(rect.top - my, 0, my - rect.bottom)
            return Math.sqrt(dx * dx + dy * dy) <= SORT_MENU_MOUSE_THRESHOLD;
        }

        if (!inRange(btnRect) && (!menuRect || !inRange(menuRect))) {
            hideSortMenu()
        }
    })

    // Navigation exit intent — warn if user has unsaved work
    const shouldWarnBeforeLeaving = computed(() => {
        if (hasUnsyncedChanges.value) return true;
        if (hasLoadedSecondPage.value) return true;
        return false;
    })

    const beforeUnloadHandler = (e) => {
        if (shouldWarnBeforeLeaving.value) {
            e.preventDefault()
            e.returnValue = ''
        }
    }

    onMounted(() => {
        clientSingleStore.stopInactivityTimeout()
        window.addEventListener('beforeunload', beforeUnloadHandler)
    })

    onBeforeRouteLeave((to, from, next) => {
        if (skipRouteLeaveGuard) {
            skipRouteLeaveGuard = false
            next()
        } else if (shouldWarnBeforeLeaving.value) {
            const confirmed = window.confirm('You have unsaved search changes. Are you sure you want to leave?')
            next(confirmed)
        } else {
            next()
        }
    })

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScrollEvents)
        window.removeEventListener('scroll', handleSortMenuScroll, true)
        window.removeEventListener('beforeunload', beforeUnloadHandler)
        caregivers.value = []

        debouncedHandleSearch.cancel()

        if (criteriaSelectionEscapeCleanup.value) {
            criteriaSelectionEscapeCleanup.value()
            criteriaSelectionEscapeCleanup.value = null
        }

        if (clientPopoverHideTimeout) {
            clearTimeout(clientPopoverHideTimeout)
            clientPopoverHideTimeout = null
        }

        resetSearchContext()
    })

    const sortOrders = computed(() => {
        const hasTravelTime = !!searchState.value.max_travel_time
        const hasWowCriteria = searchState.value.criterias.some(c => c.factor === 'wow')
        const hasAvailability = searchState.value.availability && searchState.value.availability.length > 0

        return [
            { value: 'smart', name: 'Smart', disabled: false, icon: 'pi pi-bolt' },
            { value: 'feedback', name: 'Feedback', disabled: false, icon: 'pi pi-thumbs-up' },
            { value: 'travel_time', name: 'Travel Time', disabled: !hasTravelTime, icon: 'pi pi-directions' },
            { value: 'wow_matching', name: 'Wow Factors', disabled: !hasWowCriteria, icon: 'pi pi-sparkles' },
            { value: 'schedule_matching', name: 'Schedule Fit', disabled: !hasAvailability, icon: 'pi pi-calendar-clock' },
            { value: 'recency', name: 'Date Added', disabled: false, icon: 'pi pi-clock' },
        ];
    })

    const sortMenuItems = computed(() => {
        const currentValue = searchState.value.sort_order?.value || searchState.value.sort_order
        const makeItem = (order) => ({
            label: order.name,
            icon: currentValue === order.value ? 'pi pi-check' : order.icon,
            disabled: order.disabled,
            class: currentValue === order.value ? 'bg-lifeworx-green-700/10 font-semibold' : '',
            command: () => handleSortMenuSelect(order)
        })

        return [
            {
                items: sortOrders.value.filter(o => o.value === 'smart').map(makeItem)
            },
            {
                label: 'Single-Factor Sort',
                items: sortOrders.value.filter(o => o.value !== 'smart').map(makeItem)
            }
        ];
    })

    const currentSortOrderName = computed(() => {
        const preferred = preferredSortOrder.value
        const isPriority = preferred && preferred !== 'smart' && isValidSortOrder(preferred)

        if (!isPriority) {
            const availableOrders = sortOrders.value.filter(o => !o.disabled && o.value !== 'smart')
            if (availableOrders.length === 1 && availableOrders[0].value === 'feedback') {
                return 'Feedback';
            }
            return 'SMART';
        }

        const currentValue = searchState.value.sort_order?.value || searchState.value.sort_order
        const found = sortOrders.value.find(o => o.value === currentValue)
        return found?.name || currentValue;
    })

    let isUserSortOrderChange = false

    function isValidSortOrder(value) {
        return sortOrders.value.some(option => option.value === value && !option.disabled);
    }

    function findSortOption(value) {
        return sortOrders.value.find(option => option.value === value);
    }

    function resolveSortOrder() {
        if (preferredSortOrder.value && isValidSortOrder(preferredSortOrder.value)) {
            return findSortOption(preferredSortOrder.value);
        }
        // Default to 'smart' since it's always enabled
        return findSortOption('smart');
    }

    // Initialize with preferred sort order from store (defaults to 'smart')
    if (preferredSortOrder.value && isValidSortOrder(preferredSortOrder.value)) {
        searchState.value.sort_order = findSortOption(preferredSortOrder.value)
    } else {
        searchState.value.sort_order = findSortOption('smart')
    }

    // Track previous enabled/disabled state of sort orders for smart mode refresh
    let previousSortOrderAvailability = null

    // When available sort options change, ensure current selection is still valid
    // If in priority mode and the selected priority becomes unavailable, fall back to smart
    watch(sortOrders, (newOptions) => {
        const currentValue = searchState.value.sort_order?.value || searchState.value.sort_order
        const preferred = preferredSortOrder.value
        const currentIsValid = newOptions.some(option => option.value === currentValue && !option.disabled)
        const preferredIsAvailable = preferred && newOptions.some(option => option.value === preferred && !option.disabled)

        // Capture enabled/disabled state of non-smart sort orders
        const newAvailability = Object.fromEntries(
            newOptions.filter(o => o.value !== 'smart').map(o => [o.value, !o.disabled])
        )

        if (!currentIsValid) {
            // Current selection is no longer available - fall back to smart or restore preferred if available
            searchState.value.sort_order = resolveSortOrder()
        } else if (preferredIsAvailable && preferred !== currentValue) {
            // Current selection is valid, but preferred is now available and different - switch to preferred
            searchState.value.sort_order = findSortOption(preferred)
        }

        previousSortOrderAvailability = newAvailability
    })

    watch(() => searchState.value.sort_order, (newSortOrder, oldSortOrder) => {
        if (newSortOrder?.value === null) {
            searchState.value.sort_order = oldSortOrder
            return;
        }
        const resolvedValue = newSortOrder?.value || newSortOrder
        const previousValue = oldSortOrder?.value || oldSortOrder

        if (resolvedValue === previousValue) {
            isUserSortOrderChange = false
            return;
        }

        if (isUserSortOrderChange) {
            preferredSortOrder.value = resolvedValue
            isUserSortOrderChange = false
        }
    })

    // Criteria selection modal related logics
    const showCriteriaSelectionModal = ref(false)
    const selectionModalRef = ref(null)

    const clientMedicalChartables = computed(() => {
        return client.value.matching_criterias.filter((criteria) => {
            if (!criteriaNameDict.value[criteria.name_id])
                return false;
            if (criteriaNameDict.value[criteria.name_id].is_medical_chartable) {
                return true;
            }
            return false;
        });
    })

    const clientMustHaves = computed(() => {
        return client.value.matching_criterias.filter((criteria) => {
            return criteria.factor === 'must_have';
        });
    })

    const clientWowFactors = computed(() => {
        return client.value.matching_criterias.filter((criteria) => {
            return criteria.factor === 'wow';
        });
    })

    function compareCriterias(clientCriterias, searchCriterias) {
        if (!clientCriterias || !searchCriterias) return false;
        if (clientCriterias.length !== searchCriterias.length) return false;

        return clientCriterias.every(clientCriteria => {
            const searchCriteria = searchCriterias.find(
                sc => sc.name_id === clientCriteria.name_id
            );

            if (!searchCriteria) return false;

            if (!clientCriteria.attributes || !searchCriteria.attributes) {
                return !clientCriteria.attributes && !searchCriteria.attributes;
            }

            if (clientCriteria.attributes.length !== searchCriteria.attributes.length) {
                return false;
            }

            return clientCriteria.attributes.every(clientAttr => {
                const searchAttr = searchCriteria.attributes.find(
                    sa => sa.name_id === clientAttr.name_id
                );

                if (!searchAttr) return false;

                const clientChoices = [...(clientAttr.selected_choice_ids || [])].sort();
                const searchChoices = [...(searchAttr.selected_choice_ids || [])].sort();

                return JSON.stringify(clientChoices) === JSON.stringify(searchChoices);
            });
        });
    }

    function resetAddressToClient() {
        if (!client.value?.address) return;
        Object.assign(searchState.value, {
            address: client.value.address.address || '',
            city: client.value.address.city || '',
            state: client.value.address.state,
            zip: client.value.address.zip
        })
    }

    const hasSameAddress = computed(() => {
        if (isPlaceholderClient.value) return true;
        return searchState.value.address === (client.value.address.address || '')
            && searchState.value.city === (client.value.address.city || '')
            && searchState.value.state === client.value.address.state
            && searchState.value.zip === client.value.address.zip;
    })

    // Client schedule snapshot — captured during hydration for comparison
    const clientSchedule = ref(null)

    const hasSameSchedule = computed(() => {
        if (isPlaceholderClient.value) return true;
        if (!clientSchedule.value) return true;

        if (searchState.value.living_preference !== clientSchedule.value.living_preference) return false;

        const searchAvail = searchState.value.availability || []
        const clientAvail = clientSchedule.value.availability || []
        if (searchAvail.length !== clientAvail.length) return false;

        const sortedSearch = [...searchAvail].sort((a, b) => a.day.localeCompare(b.day))
        const sortedClient = [...clientAvail].sort((a, b) => a.day.localeCompare(b.day))
        if (JSON.stringify(sortedSearch) !== JSON.stringify(sortedClient)) return false;

        if (!!searchState.value.alternate_weekends_only !== !!clientSchedule.value.alternate_weekends_only) return false;
        if (!!searchState.value.exact_match !== !!clientSchedule.value.exact_match) return false;
        if (!!searchState.value.flexible_fill_in !== !!clientSchedule.value.flexible_fill_in) return false;

        return true;
    })

    function resetScheduleToClient() {
        if (!clientSchedule.value) return;
        searchState.value.living_preference = clientSchedule.value.living_preference
        localizedAvailability.value = deepCopy(clientSchedule.value.availability)
        searchState.value.availability = localizedAvailability.value
        searchState.value.alternate_weekends_only = clientSchedule.value.alternate_weekends_only
        searchState.value.exact_match = clientSchedule.value.exact_match
        searchState.value.flexible_fill_in = clientSchedule.value.flexible_fill_in

        // Force the schedule component to remount so its internal state
        // (toggles, day checkboxes) re-initializes from the restored props.
        scheduleComponentKey.value++
    }

    const resetScheduleHovered = ref(false)
    const scheduleComponentKey = ref(0)
    const scheduleContainerRef = ref(null)
    const scheduleSearchRef = ref(null)

    function startSchedulePreview() {
        isSchedulePreviewActive = true
        resetScheduleHovered.value = true
    }

    function endSchedulePreview() {
        resetScheduleHovered.value = false
        nextTick(() => {
            nextTick(() => {
                isSchedulePreviewActive = false
            })
        })
    }

    const schedulePreviewAvailability = computed(() => {
        if (resetScheduleHovered.value && clientSchedule.value) {
            return deepCopy(clientSchedule.value.availability);
        }
        return localizedAvailability.value;
    })

    const schedulePreviewLivingPreference = computed(() => {
        if (resetScheduleHovered.value && clientSchedule.value) {
            return clientSchedule.value.living_preference;
        }
        return searchState.value.living_preference;
    })

    const hasValidAddressFields = computed(() => {
        const { address, city, state, zip } = searchState.value

        if (!address && !city && !state && !zip) return true;

        if (address) {
            return !!(city && state && zip);
        }

        if (city) {
            return !!(state || zip);
        }

        if (zip) return true;

        if (state) return true;

        return false;
    })

    const isAddressFieldsEmpty = computed(() => {
        const { address, city, state, zip } = searchState.value
        return !address && !city && !state && !zip;
    })

    const hasOnlyState = computed(() => {
        const { address, city, state, zip } = searchState.value
        return !!(state && !address && !city && !zip);
    })

    const isTravelTimeDisabled = computed(() => {
        if (!hasValidAddressFields.value) return true;

        if (isAddressFieldsEmpty.value) return true;

        if (hasOnlyState.value) return true;

        return false;
    })

    const isInStateOnlyDisabled = computed(() => {
        return !searchState.value.state;
    })

    const hasSameMustHaves = computed(() => {
        if (isPlaceholderClient.value) return true;
        return compareCriterias(clientMustHaves.value, searchMustHaves.value);
    })

    const hasSameWowFactors = computed(() => {
        if (isPlaceholderClient.value) return true;
        return compareCriterias(clientWowFactors.value, searchWowFactors.value);
    })

    // Hover states for criteria reset buttons
    const resetMustHavesHovered = ref(false)
    const resetWowFactorsHovered = ref(false)
    const mustHavesCriteriaRef = ref(null)
    const wowFactorsCriteriaRef = ref(null)

    function smoothTransitionHeight(componentRef, callback, { onlyGrow = false } = {}) {
        const el = componentRef?.$el || (componentRef instanceof HTMLElement ? componentRef : null)
        if (!el) {
            callback()
            return;
        }

        const startHeight = el.offsetHeight
        el.style.height = startHeight + 'px'
        el.style.overflow = 'hidden'
        el.style.transition = 'height 150ms ease-out'

        callback()

        nextTick(() => {
            el.style.height = 'auto'
            const endHeight = el.offsetHeight

            if (onlyGrow && endHeight <= startHeight) {
                el.style.height = startHeight + 'px'
                return;
            }

            el.style.height = startHeight + 'px'
            el.offsetHeight // eslint-disable-line no-unused-expressions
            el.style.height = endHeight + 'px'

            const onEnd = () => {
                el.style.height = ''
                el.style.overflow = ''
                el.style.transition = ''
                el.removeEventListener('transitionend', onEnd)
            }
            el.addEventListener('transitionend', onEnd)
        })
    }

    let skipCriteriaWatch = false

    function animateCriteriaContainers(callback) {
        skipCriteriaWatch = true

        const refs = [mustHavesCriteriaRef.value, wowFactorsCriteriaRef.value]
        const elements = refs
            .map(r => r?.$el || (r instanceof HTMLElement ? r : null))
            .filter(Boolean)

        const startHeights = elements.map(el => el.offsetHeight)
        elements.forEach((el, i) => {
            el.style.height = startHeights[i] + 'px'
            el.style.overflow = 'hidden'
            el.style.transition = 'height 150ms ease-out'
        })

        callback()

        nextTick(() => {
            elements.forEach((el, i) => {
                el.style.height = 'auto'
                const endHeight = el.offsetHeight

                if (endHeight === startHeights[i]) {
                    el.style.height = ''
                    el.style.overflow = ''
                    el.style.transition = ''
                    return;
                }

                el.style.height = startHeights[i] + 'px'
                el.offsetHeight // eslint-disable-line no-unused-expressions
                el.style.height = endHeight + 'px'

                const onEnd = () => {
                    el.style.height = ''
                    el.style.overflow = ''
                    el.style.transition = ''
                    el.removeEventListener('transitionend', onEnd)
                }
                el.addEventListener('transitionend', onEnd)
            })
        })
    }

    watch(() => {
        return (searchState.value.criterias || []).map(c => c.name_id + ':' + c.factor).sort().join(',');
    }, () => {
        if (skipCriteriaWatch) {
            skipCriteriaWatch = false
            return;
        }

        const refs = [mustHavesCriteriaRef.value, wowFactorsCriteriaRef.value]
        const elements = refs
            .map(r => r?.$el || (r instanceof HTMLElement ? r : null))
            .filter(Boolean)
        const startHeights = elements.map(el => el.offsetHeight)

        nextTick(() => {
            elements.forEach((el, i) => {
                // Clear any stuck inline styles from a previous animation so
                // offsetHeight reflects the true content height after the DOM update.
                el.style.height = 'auto'
                el.style.overflow = ''
                const endHeight = el.offsetHeight

                if (endHeight === startHeights[i]) {
                    el.style.height = ''
                    el.style.transition = ''
                    return;
                }

                el.style.height = startHeights[i] + 'px'
                el.style.overflow = 'hidden'
                el.style.transition = 'height 150ms ease-out'
                el.offsetHeight // eslint-disable-line no-unused-expressions
                el.style.height = endHeight + 'px'

                const onEnd = () => {
                    el.style.height = ''
                    el.style.overflow = ''
                    el.style.transition = ''
                    el.removeEventListener('transitionend', onEnd)
                }
                el.addEventListener('transitionend', onEnd)
            })
        })
    })

    function resetCriteriasToClient(type, clientCriterias) {
        animateCriteriaContainers(() => {
            const clientNameIds = (clientCriterias || []).map(c => c.name_id)

            searchState.value.criterias = searchState.value.criterias.filter(
                criteria => criteria.factor !== type && !clientNameIds.includes(criteria.name_id)
            )

            if (clientCriterias && clientCriterias.length > 0) {
                searchState.value.criterias = [
                    ...searchState.value.criterias,
                    ...deepCopy(clientCriterias)
                ];
            }
        })
    }

    function removeSearchCriteria(criteriaNameId) {
        animateCriteriaContainers(() => {
            searchState.value.criterias = searchState.value.criterias.filter(criteria => criteria.name_id !== criteriaNameId);
        })
    }

    function moveCriteriaToFactor(criteriaNameId, targetFactor) {
        animateCriteriaContainers(() => {
            const criteria = searchState.value.criterias.find(c => c.name_id === criteriaNameId)
            if (criteria && criteria.factor !== targetFactor) {
                criteria.factor = targetFactor
            }
        })
    }

    function relaxMustHaves(executeSearch = true) {
        animateCriteriaContainers(() => {
            searchState.value.criterias.forEach(criteria => {
                if (criteria.factor === 'must_have') {
                    criteria.factor = 'wow'
                }
            })
        })
        if (executeSearch) {
            handleSearch()
        }
    }

    const travelTimeSteps = ['20', '40', '60', '120']

    function increaseTravelTime() {
        const current = searchState.value.max_travel_time
        const currentIndex = travelTimeSteps.indexOf(current)
        if (currentIndex !== -1 && currentIndex < travelTimeSteps.length - 1) {
            searchState.value.max_travel_time = travelTimeSteps[currentIndex + 1]
            handleSearch()
        }
    }

    const criteriaSelectionEscapeCleanup = ref(null)

    const toggleCriteriaSelectionModal = (scrollToNameId = null) => {

        const isOpening = !showCriteriaSelectionModal.value
        showCriteriaSelectionModal.value = !showCriteriaSelectionModal.value

        if (scrollToNameId) {
            setTimeout(() => {
                selectionModalRef.value.scrollToCriteria(scrollToNameId)
            }, 50)
        }

        if (isOpening) {
            if (criteriaSelectionEscapeCleanup.value) {
                criteriaSelectionEscapeCleanup.value()
                criteriaSelectionEscapeCleanup.value = null
            }
            criteriaSelectionEscapeCleanup.value = util.listenforEscapeKeyPress(() => {
                showCriteriaSelectionModal.value = false
                if (criteriaSelectionEscapeCleanup.value) {
                    criteriaSelectionEscapeCleanup.value()
                    criteriaSelectionEscapeCleanup.value = null
                }
            })
        } else if (criteriaSelectionEscapeCleanup.value) {
             criteriaSelectionEscapeCleanup.value()
             criteriaSelectionEscapeCleanup.value = null
        }

    }

    // Travel time related logics
    const maxTravelTimes = [
        { value: '20', name: '20 min' },
        { value: '40', name: '40 min' },
        { value: '60', name: '60 min' },
        { value: '120', name: '120 min' }
    ]

    const locationStates = ref([
        {
            value: null,
            name: ""
        }
    ])



    watch(config, (value) => {
        locationStates.value = value.states.map(item => ({
            value: item.id,
            name: item.id
        }))
    }, { deep: true, immediate: true })

    function addressFill(place) {
        const addressData = parseAddressComponents(place, { stateAsObject: false, includeAddress2: false })
        Object.assign(searchState.value, addressData)
    }

    function handleAddressUpdate(value) {
        if (typeof value === 'object' && value.place) {
            addressFill(value.place)
        } else {
            searchState.value.address = value
        }
    }

    watch(isTravelTimeDisabled, (disabled) => {
        if (disabled) {
            searchState.value.max_travel_time = null
        } else {
            searchState.value.max_travel_time = '40'
        }
    })

    watch(() => searchState.value.state, (state) => {
        if (!state) {
            searchState.value.in_state_only = false
        }
    })

    watch(hasOnlyState, (onlyState, previousOnlyState) => {
        if (onlyState && !previousOnlyState) {
            searchState.value.in_state_only = true
        }
    })

    // Client hydration logic - applies client context to search state on initial load and when client changes
    watch(client, (newClient) => {
        if (!newClient || 'fe_generated' in newClient) {
            return;
        }

        // Skip hydration if the current route has no client context — prevents
        // stale store data from re-triggering search after navigating away and back.
        const routeClientId = props.client_id || new URLSearchParams(window.location.search).get('client_id')
        if (!routeClientId) {
            return;
        }

        // Guard against stale client data — only hydrate if the store's client
        // matches the client the route is actually requesting.
        if (String(newClient.id) !== String(routeClientId)) {
            return;
        }

        searchState.value.client_id = newClient.id

        // Handle care schedule
        if (shouldApplyClientSearchCareSchedule && newClient.search_care_schedule) {
            const schedule = newClient.search_care_schedule
            searchState.value.living_preference = schedule.living_preference

            localizedAvailability.value = schedule.days.map(slot => ({
                day: slot.day,
                start_hour: slot.start_hour != null ? parseInt(slot.start_hour) : DEFAULT_LIVE_OUT_START_HOUR,
                end_hour: slot.end_hour != null ? parseInt(slot.end_hour) : DEFAULT_LIVE_OUT_END_HOUR
            }))

            // Add availability to searchState
            searchState.value.availability = localizedAvailability.value

            // Snapshot the client schedule for comparison/revert
            clientSchedule.value = {
                living_preference: schedule.living_preference,
                availability: deepCopy(localizedAvailability.value),
                alternate_weekends_only: false,
                exact_match: false,
                flexible_fill_in: false,
            }

            shouldApplyClientSearchCareSchedule = false
        } else if (shouldApplyClientSearchCareSchedule) {
            // Client has no care schedule — capture empty baseline for comparison/revert
            clientSchedule.value = {
                living_preference: '',
                availability: [],
                alternate_weekends_only: false,
                exact_match: false,
                flexible_fill_in: false,
            }
            shouldApplyClientSearchCareSchedule = false
        }

        // Handle location
        if (shouldApplyClientLocation) {
            searchState.value.address = newClient.address.address || ''
            searchState.value.city = newClient.address.city || ''
            searchState.value.state = newClient.address.state
            if (newClient.address.zip) {
                searchState.value.zip = newClient.address.zip
            }
            if (newClient.address.zip || (newClient.address.city && newClient.address.state)) {
                searchState.value.max_travel_time = '40'
            }
            shouldApplyClientLocation = false
        }

        // Handle criteria
        if (shouldApplyClientCriteria && newClient.matching_criterias) {
            searchState.value.criterias = newClient.matching_criterias
                .filter(criteria => criteria.factor === 'must_have' || criteria.factor === 'wow')
                .map(criteria => ({
                    name_id: criteria.name_id,
                    selected: true,
                    factor: criteria.factor,
                    attributes: criteria.attributes
                }))
            shouldApplyClientCriteria = false
        }

        // Handle service types
        if (shouldApplyClientServiceTypes && newClient.service_types && newClient.service_types.length > 0) {
            searchState.value.service_types = newClient.service_types
            shouldApplyClientServiceTypes = false
        } else if (shouldApplyClientServiceTypes) {
            // Client has no service types — reset to default
            searchState.value.service_types = ['elder_care']
            shouldApplyClientServiceTypes = false
        }

        if (!searchState.value.availability) {
            searchState.value.availability = localizedAvailability.value
        }
        searchState.value.alternate_weekends_only = searchState.value.alternate_weekends_only ?? false
        searchState.value.exact_match = searchState.value.exact_match ?? false
        searchState.value.flexible_fill_in = searchState.value.flexible_fill_in ?? false
        searchState.value.in_state_only = searchState.value.in_state_only ?? false

        if (!hasDefaultSearchState.value) {
            handleSearch()
        }
    }, { immediate: true })

    const handleLivingPreferenceChange = (newPreference) => {
        if (isSchedulePreviewActive) return;
        searchState.value.living_preference = newPreference
    }

    // Quick Search Component
    const quickSearchRef = ref(false)
    const showClientSelectionConfirmationDialog = ref(false)
    const pendingClientId = ref(null)
    const clientBarOverrideLabel = ref(null)

    const isPristine = computed(() => {
        return isPlaceholderClient.value && hasDefaultSearchState.value;
    })

    const clientProfileUrl = computed(() => {
        if (!isPlaceholderClient.value && client.value) {
            return router.resolve({ name: 'client.profile', params: { client: client.value.id } }).href;
        }
        return '#';
    })

    // Client Picker
    const clientPickerLabel = computed(() => {
        if (loading.value) {
            return 'Loading...';
        }
        if (!isPlaceholderClient.value && client.value) {
            return util.displayClientName(client.value.first_name, client.value.last_name, client.value.type) || 'Select a Profile';
        }
        return 'Select a Profile';
    })

    const handleClientBarClick = () => {
        quickSearchRef.value = true
    }

    let clientPopoverHideTimeout = null
    const clientSummaryCardVisible = ref(false)

    const showClientSummaryCard = () => {
        if (clientPopoverHideTimeout) {
            clearTimeout(clientPopoverHideTimeout)
            clientPopoverHideTimeout = null
        }
        clientSummaryCardVisible.value = true
    }

    const hideClientSummaryCard = () => {
        clientSummaryCardVisible.value = false
        if (clientPopoverHideTimeout) {
            clearTimeout(clientPopoverHideTimeout)
            clientPopoverHideTimeout = null
        }
    }

    const scheduleHideClientSummaryCard = (delay = 150) => {
        if (clientPopoverHideTimeout) {
            clearTimeout(clientPopoverHideTimeout)
        }
        clientPopoverHideTimeout = setTimeout(() => {
            clientSummaryCardVisible.value = false
            clientPopoverHideTimeout = null
        }, delay)
    }

    const handleClientButtonMouseleave = (event) => {
        if (isPlaceholderClient.value) return
        clientBarOverrideLabel.value = null

        // If cursor moved downward (toward the card), use a delay to bridge the gap
        const rect = event.currentTarget.getBoundingClientRect()
        const isMovingDown = event.clientY >= rect.bottom - 2

        if (isMovingDown) {
            scheduleHideClientSummaryCard(200)
        } else {
            hideClientSummaryCard()
        }
    }

    const handleClearSearch = () => {
        if (!isPlaceholderClient.value) {
            skipRouteLeaveGuard = true
            clearClient()
        } else {
            resetSearchContext()
        }
    }

    const hasSearchChangesFromClient = computed(() => {
        if (isPlaceholderClient.value) return !hasDefaultSearchState.value;
        return !hasSameAddress.value || !hasSameSchedule.value || !hasSameMustHaves.value || !hasSameWowFactors.value
    })

    const handleQuickSearchSelect = (result) => {
        if (hasSearchChangesFromClient.value) {
            pendingClientId.value = result.id
            showClientSelectionConfirmationDialog.value = true
            return;
        }
        navigateToClient(result.id)
    }

    const navigateToClient = (clientId) => {
        if (String(clientId) === String(props.client_id)) {
            loadClientForSearch(clientId)
            return;
        }
        router.push({ name: 'search.caregivers.client', params: { client_id: clientId } })
    }

    const handleClientSelectionConfirmChange = () => {
        showClientSelectionConfirmationDialog.value = false
        if (pendingClientId.value) {
            skipRouteLeaveGuard = true
            navigateToClient(pendingClientId.value)
        }
    }

    const handleClientSelectionCancelChange = () => {
        showClientSelectionConfirmationDialog.value = false
        pendingClientId.value = null
    }

    const selectedCaregiverStore = ref(null)
    function handleSelectCaregiver(caregiverId) {
        selectedCaregiverStore.value = caregiverSearchStore.selectCaregiver(caregiverId)
    }

    function handleSortMenuSelect(order) {
        isUserSortOrderChange = true
        searchState.value.sort_order = findSortOption(order.value) || resolveSortOrder()
        handleSearch()
    }

</script>

<template>
    <div class="-mt-20 top-32 bg-gray-50">
        <LatteDrawer title="Caregiver Search">

            <template #sidebar>
                <div class="h-max flex flex-col px-6 pt-24 pb-28 object-center gap-6 relative">

                    <div>
                        <div class="flex justify-between">
                            <div class="flex items-center gap-2">
                                <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Service Location</span>
                                <span
                                    v-if="!hasSameAddress"
                                    class="text-xs text-stone-500 italic pb-1"
                                >(Edited)</span>
                            </div>
                            <button
                                v-if="!isPlaceholderClient"
                                type="button"
                                :class="[
                                    'size-6 p-1 -mt-1 mr-0.5 rounded transition-colors',
                                    hasSameAddress ? 'opacity-10 cursor-default' : 'cursor-pointer hover:bg-lifeworx-blue-800/10',
                                ]"
                                @click="!hasSameAddress && resetAddressToClient()"
                            >
                                <ArrowUturnLeftIcon class="h-4 w-4 text-lifeworx-blue-800" />
                            </button>
                        </div>
                        <div class="w-full mb-2">
                            <InputLabel for="search-address">
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <AddressAutocomplete
                                    v-else
                                    v-model="searchState.address"
                                    @update:modelValue="handleAddressUpdate"
                                    placeholder="Address"
                                    :input_class="!hasValidAddressFields ? '!border-lifeworx-red-500 bg-lifeworx-red-500/5 border-2 text-gray-900 placeholder:text-stone-400 !focus:ring-lifeworx-red-500 !focus:border-lifeworx-red-500' : 'border-stone-400 text-gray-900 placeholder:text-stone-400 focus:ring-lifeworx-blue-600 focus:border-lifeworx-blue-600'"
                                />
                            </InputLabel>
                        </div>
                        <div class="grid grid-cols-4 gap-2 mb-6">
                            <InputLabel
                                for="search-city"
                                class="col-span-2"
                            >
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <TextInput
                                    v-else
                                    v-model="searchState.city"
                                    type="text"
                                    placeholder="City"
                                    :class="{ '!border-lifeworx-red-500 border-2 bg-lifeworx-red-500/5': !hasValidAddressFields }"
                                />
                            </InputLabel>
                            <InputLabel for="search-state">
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <LwxSimpleSelect
                                    v-else
                                    id="search-state"
                                    :showNullOption="true"
                                    v-model="searchState.state"
                                    :options="locationStates"
                                    placeholder="State"
                                    :class="{ '!border-lifeworx-red-500 border-2 h-full !bg-lifeworx-red-500/5': !hasValidAddressFields }"
                                />
                            </InputLabel>
                            <InputLabel for="search-zip">
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <TextInput
                                    v-else
                                    v-model="searchState.zip"
                                    type="text"
                                    maxlength="5"
                                    inputmode="numeric"
                                    @keypress="($event) => {
                                        if (!/[0-9]/.test($event.key)) {
                                            $event.preventDefault()
                                        }
                                    }"
                                    placeholder="Zip"
                                    :class="{ '!border-lifeworx-red-500 border-2 h-full bg-lifeworx-red-500/5': !hasValidAddressFields }"
                                />
                            </InputLabel>
                            <div class="mb-2 col-start-3 col-span-2">
                                <label class="flex items-center justify-center gap-2">
                                    <LwxSwitch
                                        v-model="searchState.in_state_only"
                                        :disabled="isInStateOnlyDisabled"
                                        :readonly="hasOnlyState"
                                        palette="blue"
                                    />
                                    <span
                                        class="text-xs font-semibold select-none"
                                        :class="isInStateOnlyDisabled ? 'text-lifeworx-blue-800/30' : (searchState.in_state_only ? 'text-lifeworx-blue-800' : 'text-stone-400')"
                                    >Show In-State Only</span>
                                </label>
                            </div>
                        </div>


                        <div class="grid grid-cols-2 gap-2">

                            <InputLabel
                                for="living_preference"
                                class="[&>span:first-child]:font-bold [&>span:first-child]:text-xs [&>span:first-child]:text-lifeworx-blue-800"
                            >
                                <span class="flex items-center gap-2 mb-1">
                                    <span class="font-bold text-xs text-lifeworx-blue-800">Living Preference</span>
                                    <span
                                        v-if="!isPlaceholderClient && clientSchedule && searchState.living_preference !== clientSchedule.living_preference"
                                        class="text-xs text-stone-500 italic font-normal"
                                    >{{ resetScheduleHovered ? '(Profile)' : '(Edited)' }}</span>
                                </span>
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <LwxSimpleSelect
                                    v-else
                                    id="living_preference"
                                    :showNullOption="false"
                                    :modelValue="schedulePreviewLivingPreference"
                                    @update:modelValue="(val) => searchState.living_preference = val"
                                    :options="stayType"
                                    :input_class="'bg-[#008AAB26] text-stone-700 !border-lifeworx-blue-600/30'"
                                />
                            </InputLabel>

                            <InputLabel
                                for="max_travel_time"
                                value="Max Travel Time"
                                :class="[
                                    '[&>span:first-child]:font-bold [&>span:first-child]:text-xs ',
                                    isTravelTimeDisabled ? '[&>span:first-child]:text-lifeworx-blue-800/30' : '[&>span:first-child]:text-lifeworx-blue-800'
                                ]"
                            >
                                <Skeleton
                                    v-if="loading"
                                    height="34px"
                                    class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20"
                                />
                                <LwxSimpleSelect
                                    v-else
                                    id="max_travel_time"
                                    v-model="searchState.max_travel_time"
                                    :options="maxTravelTimes"
                                    :showNullOption="false"
                                    :disabled="isTravelTimeDisabled"
                                />
                            </InputLabel>

                        </div>

                    </div>

                    <div class="grid gap-4 relative">

                        <!-- Connector bracket between living preference and care schedule -->
                        <svg
                            class="absolute -top-11 -left-2 h-24 w-2 text-lifeworx-blue-600/30"
                            viewBox="0 0 8 96"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M8 1 H1 V95 H8" stroke="currentColor" stroke-width="1.5" fill="none" />
                        </svg>

                        <div>
                            <div class="flex justify-between items-center">
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Care Schedule</span>
                                    <span
                                        v-if="!hasSameSchedule"
                                        class="text-xs text-stone-500 italic pb-1"
                                    >{{ resetScheduleHovered ? '(Profile)' : '(Edited)' }}</span>
                                </div>
                                <div class="flex items-center gap-2 -mt-1">
                                    <template v-if="searchState.living_preference">
                                        <button
                                            class="text-xs font-semibold text-lifeworx-orange-500 underline underline-offset-1 hover:text-lifeworx-orange-800 cursor-pointer"
                                            @click="scheduleSearchRef?.enableWeekdays()"
                                        >Weekdays</button>
                                        <button
                                            class="text-xs font-semibold text-lifeworx-orange-500 underline underline-offset-1 hover:text-lifeworx-orange-800 cursor-pointer"
                                            @click="scheduleSearchRef?.enableWeekends()"
                                        >Weekends</button>
                                        <button
                                            class="text-xs font-semibold text-lifeworx-orange-500 underline underline-offset-1 hover:text-lifeworx-orange-800 cursor-pointer"
                                            @click="scheduleSearchRef?.enableAllDays()"
                                        >All</button>
                                    </template>
                                    <button
                                        v-if="!isPlaceholderClient"
                                        type="button"
                                        :class="[
                                            'size-6 p-1 -mt-1 mr-0.5 rounded transition-colors',
                                            hasSameSchedule ? 'opacity-10 cursor-default' : 'cursor-pointer hover:bg-lifeworx-blue-800/10',
                                            resetScheduleHovered && !hasSameSchedule ? 'bg-lifeworx-blue-800/10' : ''
                                        ]"
                                        @mouseenter="startSchedulePreview"
                                        @mouseleave="endSchedulePreview"
                                        @click="!hasSameSchedule && resetScheduleToClient()"
                                    >
                                        <ArrowUturnLeftIcon class="h-4 w-4 text-lifeworx-blue-800" />
                                    </button>
                                </div>
                            </div>
                            <div v-if="loading">
                                <Skeleton height="120px" class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20" />
                            </div>
                            <div v-else ref="scheduleContainerRef">
                                <LatteCareScheduleSearch
                                    ref="scheduleSearchRef"
                                    :key="scheduleComponentKey"
                                    @change:careSchedule="handleCareScheduleChange"
                                    @update:livingPreference="handleLivingPreferenceChange"
                                    :availability="schedulePreviewAvailability"
                                    :livingPreference="schedulePreviewLivingPreference"
                                    :defaultStartHour="DEFAULT_LIVE_OUT_START_HOUR"
                                    :defaultEndHour="DEFAULT_LIVE_OUT_END_HOUR"
                                    :hasMultipleShiftsPerDay="client?.search_care_schedule?.has_multiple_shifts_per_day"
                                />
                            </div>
                        </div>

                    </div>

                    <div class="grid">
                        <div class="col-span-1 z-10">
                            <div class="flex justify-between items-center">
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Must-Haves</span>
                                    <span
                                        v-if="!hasSameMustHaves"
                                        class="text-xs text-stone-500 italic pb-1"
                                    >{{ resetMustHavesHovered ? '(Profile)' : '(Edited)' }}</span>
                                </div>
                                <button
                                    v-if="!isPlaceholderClient"
                                    type="button"
                                    :class="[
                                        'size-6 p-1 -mt-1 mr-0.5 rounded transition-colors',
                                        hasSameMustHaves ? 'opacity-10 cursor-default' : 'cursor-pointer hover:bg-lifeworx-blue-800/10',
                                        resetMustHavesHovered && !hasSameMustHaves ? 'bg-lifeworx-blue-800/10' : ''
                                    ]"
                                    @mouseenter="smoothTransitionHeight(mustHavesCriteriaRef, () => resetMustHavesHovered = true, { onlyGrow: true })"
                                    @mouseleave="smoothTransitionHeight(mustHavesCriteriaRef, () => resetMustHavesHovered = false)"
                                    @click="hasSameMustHaves ? null : resetCriteriasToClient('must_have', clientMustHaves)"
                                >
                                    <ArrowUturnLeftIcon class="h-4 w-4 text-lifeworx-blue-800" />
                                </button>
                            </div>
                            <div v-if="loading" class="flex flex-col gap-2">
                                <Skeleton height="42px" class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20" />
                            </div>
                            <LatteClientCriteriaList
                                v-else
                                ref="mustHavesCriteriaRef"
                                :criterias="resetMustHavesHovered ? clientMustHaves : searchMustHaves"
                                type="mustHaves"
                                @toggle:criteria-selection-modal="toggleCriteriaSelectionModal"
                                @remove:criteria="removeSearchCriteria"
                                @drop:criteria="(nameId) => moveCriteriaToFactor(nameId, 'must_have')"
                            />
                        </div>

                        <div
                            v-if="searchMustHaves.length > 5"
                            class="bg-[#cc5a2a15] border-t-0 rounded-b-md mx-4 p-2 flex items-center justify-center border border-dashed border-lifeworx-orange-500/20 text-xs"
                        >
                            <p class="text-center text-lifeworx-red-500/80 font-semibold"><span class="font-bold text-lifeworx-red-500/80">Warning:</span> Too many must-haves may limit your results.</p>
                        </div>

                        <div class="col-span-1 -mb-6 flex justify-end items-center pr-8">
                            <div
                                :class="[
                                    'h-12 z-0',
                                    {
                                        'bg-gradient-to-b from-lifeworx-orange-600/20 to-lifeworx-purple-600/20 hover:from-lifeworx-orange-600/30 hover:to-lifeworx-purple-600/50': searchMustHaves.length > 0,
                                        'bg-gray-200': searchMustHaves.length === 0
                                    }
                                ]"
                            >
                                <div class="flex h-full bg-transparent border-l-2 border-gray-50 border-dotted items-center justify-center border-separate">
                                    <div class="text-gray-800 bg-gray-50 h-full flex items-center justify-center">
                                        <button
                                            type="button"
                                            :disabled="searchMustHaves.length === 0"
                                            :class="[
                                                'text-xs transition-colors mx-2 font-semibold',
                                                searchMustHaves.length === 0 ? 'text-stone-300 ' : 'text-lifeworx-orange-500 font-semibold underline hover:text-lifeworx-purple-600 cursor-pointer'
                                            ]"
                                            @click="relaxMustHaves(false)"
                                        ><ArrowDownIcon class="h-4 w-4 mr-0.5 inline-block" />Relax Must-Haves</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-span-1 z-10">
                            <div class="flex justify-between items-center">
                                <div class="flex items-center gap-2">
                                    <span class="font-bold text-sm text-lifeworx-blue-800 pb-1">Wow-Factors</span>
                                    <span
                                        v-if="!hasSameWowFactors"
                                        class="text-xs text-stone-500 italic pb-1"
                                    >{{ resetWowFactorsHovered ? '(Profile)' : '(Edited)' }}</span>
                                </div>
                                <button
                                    v-if="!isPlaceholderClient"
                                    type="button"
                                    :class="[
                                        'size-6 p-1 -mt-1 mr-0.5 rounded transition-colors',
                                        hasSameWowFactors ? 'opacity-10 cursor-default' : 'cursor-pointer hover:bg-lifeworx-blue-800/10',
                                        resetWowFactorsHovered && !hasSameWowFactors ? 'bg-lifeworx-blue-800/10' : ''
                                    ]"
                                    @mouseenter="smoothTransitionHeight(wowFactorsCriteriaRef, () => resetWowFactorsHovered = true, { onlyGrow: true })"
                                    @mouseleave="smoothTransitionHeight(wowFactorsCriteriaRef, () => resetWowFactorsHovered = false)"
                                    @click="hasSameWowFactors ? null : resetCriteriasToClient('wow', clientWowFactors)"
                                >
                                    <ArrowUturnLeftIcon class="h-4 w-4 text-lifeworx-blue-800" />
                                </button>
                            </div>
                            <div v-if="loading" class="flex flex-col gap-2">
                                <Skeleton height="42px" class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20" />
                            </div>
                            <LatteClientCriteriaList
                                v-else
                                ref="wowFactorsCriteriaRef"
                                :criterias="resetWowFactorsHovered ? clientWowFactors : searchWowFactors"
                                type="wowFactors"
                                @toggle:criteria-selection-modal="toggleCriteriaSelectionModal"
                                @remove:criteria="removeSearchCriteria"
                                @drop:criteria="(nameId) => moveCriteriaToFactor(nameId, 'wow')"
                            />
                        </div>

                        <div class="col-span-1 z-10 mt-6">
                            <div class="flex items-center gap-2">
                                <span class="font-bold text-sm text-lifeworx-blue-800 pb-1 inline-block">Service Types</span>
                            </div>
                            <div v-if="loading" class="flex flex-col gap-2 mt-2">
                                <Skeleton height="34px" class="border bg-lifeworx-blue-800/10 border-lifeworx-blue-800/20" />
                            </div>
                            <InputLabel
                                v-else
                                for="search-servicetypes"
                                class="mt-2"
                            >
                                <MultiSelect
                                    id="search-servicetypes"
                                    v-model="searchState.service_types"
                                    :options="SERVICE_TYPES_OPTIONS"
                                    optionLabel="name"
                                    optionValue="id"
                                    :maxSelectedLabels="3"
                                    class="w-full"
                                    display="chip"
                                    :pt="{
                                        root: { class: 'text-xs h-[34px]' },
                                        overlay: { class: 'text-sm !z-modal' },
                                        header: { class: 'hidden'}
                                    }"
                                />
                            </InputLabel>
                        </div>
                    </div>



                </div>

                <div class="fixed shrink-0 max-w-md lg:block lg:w-[28rem] 2xl:w-[28rem] pt-4 top-16 left-0 right-0 p-4 bg-gradient-to-b from-white/90 to-transparent pb-5 z-50">
                    <div :class="[
                        'w-full max-h-12 flex items-center rounded-lg divide-x overflow-clip shadow-[0px_20px_20px_-3px_#FFFFFF]',
                        isPlaceholderClient
                            ? 'text-lifeworx-blue-700 bg-lifeworx-blue/5 border border-dashed border-lifeworx-blue-600/30 divide-lifeworx-blue-600/20'
                            : 'text-white bg-lifeworx-blue-700 divide-lifeworx-blue-600/50'
                    ]">
                        <button
                            v-if="!isPlaceholderClient"
                            type="button"
                            class="inline-flex items-center justify-center px-4 py-4 focus:outline-none focus:ring-0 transition ease-in-out duration-150 bg-lifeworx-blue-800/25 hover:bg-lifeworx-blue-800/50 cursor-pointer"
                            @click="handleClearSearch"
                            @mouseenter="clientBarOverrideLabel = 'Reset Search'"
                            @mouseleave="clientBarOverrideLabel = null"
                        >
                            <XMarkIcon class="h-4 w-4" />
                        </button>
                        <button
                            type="button"
                            :class="[
                                'flex-grow flex items-center justify-center gap-2 px-4 py-4 uppercase font-bold text-sm text-center tracking-wider focus:outline-none focus:ring-0 transition ease-in-out duration-150',
                                isPlaceholderClient
                                    ? 'hover:bg-lifeworx-blue-700/20 text-lifeworx-blue-600'
                                    : 'hover:bg-lifeworx-blue-600 focus:bg-lifeworx-blue-700 active:bg-lifeworx-blue-800'
                            ]"
                            @click="handleClientBarClick"
                            @mouseenter="!isPlaceholderClient ? (clientBarOverrideLabel = 'Switch Profile', showClientSummaryCard()) : null"
                            @mouseleave="handleClientButtonMouseleave"
                        >
                            <IconPersonSearch16Filled v-if="isPlaceholderClient" class="h-4 w-4" />
                            {{ clientBarOverrideLabel ?? clientPickerLabel }}
                        </button>
                        <a
                            v-if="!isPlaceholderClient"
                            :href="clientProfileUrl"
                            target="_blank"
                            class="inline-flex items-center justify-center px-4 py-4 focus:outline-none focus:ring-0 transition ease-in-out duration-150 hover:bg-lifeworx-blue-600 cursor-pointer"
                            @mouseenter="clientBarOverrideLabel = 'Open Profile'"
                            @mouseleave="clientBarOverrideLabel = null"
                        >
                            <ArrowTopRightOnSquareIcon class="h-4 w-4" />
                        </a>
                    </div>

                    <div
                        v-if="clientSummaryCardVisible && !isPlaceholderClient && client"
                        ref="clientCardRef"
                        class="mx-4 bg-white rounded-b-lg shadow-2xl border border-gray-200 p-1 relative"
                        @mouseenter="showClientSummaryCard"
                        @mouseleave="(event) => {
                            if (event.relatedTarget?.closest?.('.v-popper__popper')) {
                                return;
                            }
                            hideClientSummaryCard()
                        }"
                    >
                        <LatteSearchClientSummaryCard
                            :client="client"
                            @close="hideClientSummaryCard()"
                        />
                    </div>
                </div>

                <div class="fixed shrink-0 max-w-md lg:block lg:w-[28rem] 2xl:w-[28rem] bottom-0 left-0 right-0 p-4 pb-1 bg-gradient-to-t from-white to-transparent via-white/75 pt-5 z-50">

                    <div :class="[
                        'w-full max-h-12 flex justify-end items-center rounded-lg text-white divide-x overflow-clip shadow-[0px_-20px_20px_-3px_#FFFFFF]',
                        hasUnsyncedChanges
                            ? 'bg-lifeworx-orange-600 divide-lifeworx-orange-500/50'
                            : 'bg-lifeworx-green-700 divide-lifeworx-green-600/50'
                    ]">
                        <button
                            ref="sortButtonRef"
                            type="button"
                            :class="[
                                'w-1/3 inline-flex items-center px-4 py-4 uppercase font-semibold text-xs text-left tracking-wider focus:outline-none transition ease-in-out duration-150 focus:ring-0',
                                hasUnsyncedChanges
                                    ? 'bg-lifeworx-orange-700/25 hover:bg-lifeworx-orange-700/50'
                                    : 'bg-lifeworx-green-800/25 hover:bg-lifeworx-green-800/50'
                            ]"
                            @click="toggleSortMenu"
                            @mouseenter="showSortMenu"
                        >
                            <div class="flex flex-col grow">
                                <span>{{ currentSortOrderName }}</span>
                            </div>
                            <ChevronUpIcon
                                :class="[
                                    'size-4 transition-transform duration-200',
                                    sortMenuOpen ? 'rotate-180' : 'rotate-0'
                                ]"
                            />
                        </button>
                        <button
                            type="button"
                            id="searchButton"
                            :class="[
                                'flex-grow flex items-center justify-between gap-2 pl-8 pr-4 py-4 uppercase font-bold text-sm tracking-wider focus:outline-none focus:ring-0 transition ease-in-out duration-150',
                                !hasValidAddressFields
                                    ? 'bg-stone-400 text-stone-200 cursor-not-allowed'
                                    : hasUnsyncedChanges
                                        ? 'hover:bg-lifeworx-orange-500 focus:bg-lifeworx-orange-600 active:bg-lifeworx-orange-700 search-pending-pulse'
                                        : 'hover:bg-lifeworx-green-600 focus:bg-lifeworx-green-700 active:bg-lifeworx-green-800 focus:ring-lifeworx-green-500'
                            ]"
                            @click="debouncedHandleSearch"
                            :disabled="!hasValidAddressFields"
                        >
                            <span v-if="isRequestPending">{{ limitOffset.offset === 0 ? 'Searching' : 'Loading' }}<span class="inline-block w-5 text-left after:animate-dot-pulse after:content-['']" /></span>
                            <span v-else>Search</span>
                            <Loader v-if="isRequestPending" variant="small" />
                            <MagnifyingGlassIcon v-else class="size-5" />
                        </button>
                    </div>

                    <div class="flex items-center justify-center gap-4 mt-2 mb-1 mx-2">
                        <label class="flex items-center gap-2 cursor-pointer">
                            <LwxSwitch
                                :modelValue="!searchState.all_candidates"
                                @update:modelValue="(val) => searchState.all_candidates = !val"
                                palette="blue"
                            />
                            <span
                                class="text-xs font-semibold select-none" :class="!searchState.all_candidates ? 'text-lifeworx-blue-800' : 'text-stone-400'"
                            >Mine Only</span>
                        </label>
                        <label class="flex items-center gap-2 cursor-pointer">
                            <LwxSwitch
                                :modelValue="searchState.status === 'all'"
                                @update:modelValue="(val) => searchState.status = val ? 'all' : 'active'"
                                palette="blue"
                            />
                            <span
                                :class="[
                                    'flex text-xs font-semibold select-none',
                                    searchState.status === 'all' ? 'text-lifeworx-blue-600' : 'text-stone-400'
                                ]"
                            >Show Inactive <IconSnooze16Filled class="ml-0.5 size-4" /></span>

                        </label>
                    </div>

                    <Menu
                        ref="sortMenuRef"
                        :model="sortMenuItems"
                        :popup="true"
                        class="sort-menu"
                        @show="sortMenuOpen = true"
                        @hide="sortMenuOpen = false"
                    />
                </div>

            </template>

            <div class="bg-gray-50">

                <CaregiverSearchResults
                    :caregivers="caregivers"
                    :selectedCaregiverStore="selectedCaregiverStore"
                    :currentSearchState="currentSearchPayload"
                    :limitOffset="limitOffset"
                    :searchFilterChanged="hasUnsyncedChanges"
                    :isSearching="isSearching"
                    :isRequestPending="isRequestPending"
                    :noMoreResult="noMoreResult"
                    :isLoadingClient="loading"
                    :totalCount="totalCount"
                    @selectCaregiver="handleSelectCaregiver"
                    @openQuickSearch="() => quickSearchRef = true"
                    @relaxMustHaves="relaxMustHaves"
                    @increaseTravelTime="increaseTravelTime"
                />

            </div>

        </LatteDrawer>

        <Modal
            :show="showCriteriaSelectionModal"
            :padding-enable="false"
            max-width="4xl"
            :verticalOffset="null"
        >
            <LatteClientCareNeedSelection
                ref="selectionModalRef"
                v-model="searchState.criterias"
                :categories="config.matching_criteria_descriptor.categories"
                :criterias="clientCriterias"
                :medicalChartSelectable="false"
                mode="search"
                @close="toggleCriteriaSelectionModal"
            />
        </Modal>

        <Modal
            :show="showClientSelectionConfirmationDialog"
            @close="handleClientSelectionCancelChange"
            max-width="lg"
        >
            <div class="pb-4">
                <div class="sm:flex sm:items-start">
                    <div class="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 sm:mx-0 sm:h-10 sm:w-10">
                        <ExclamationTriangleIcon
                            class="h-6 w-6 text-yellow-600"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                        <h3 class="text-base font-semibold leading-6 text-lifeworx-purple-800">
                            Change Client
                        </h3>
                        <div class="mt-2">
                            <p class="text-sm text-stone-500">
                                Are you sure you want to select this client? Your current search filters will be lost.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="sm:flex sm:flex-row-reverse">
                <PrimaryButton
                    class="w-full sm:w-auto sm:ml-3"
                    @click="handleClientSelectionConfirmChange"
                >
                    Confirm
                </PrimaryButton>

                <SecondaryButton
                    class="mt-3 w-full sm:mt-0 sm:w-auto"
                    @click="handleClientSelectionCancelChange"
                >
                    Cancel
                </SecondaryButton>
            </div>
        </Modal>

        <QuickSearch
            v-model="quickSearchRef"
            :scope="['lead', 'prospect', 'client']"
            @select="handleQuickSearchSelect"
        />

    </div>
</template>

<style>

    .v-popper--theme-health-conditions .v-popper__inner {
        @apply bg-white shadow-lg rounded-lg border border-gray-200 max-w-[400px];
    }

    .v-popper--theme-health-conditions .v-popper__arrow-outer {
        @apply border-gray-200;
    }

    .v-popper--theme-health-conditions .v-popper__arrow-inner {
        @apply border-white;
    }

    @keyframes search-pending-pulse {
        0%, 100% { background-color: transparent; }
        50% { background-color: rgb(220 120 62 / 0.25); }
    }

    .search-pending-pulse {
        animation: search-pending-pulse 1.5s ease-in-out infinite;
    }

    .sort-menu li:first-child.p-menu-submenu-label:empty {
        display: none;
    }

</style>
